import React, { createContext, useContext, useState } from 'react';
import AnimateHeight, { Height } from 'react-animate-height';
import { IconChevronDown, IconChevronUp } from '@pentabd/icons';

export interface MenuProps {
  children: JSX.Element | JSX.Element[];
}

export interface MenuHeadProps {
  children: JSX.Element | JSX.Element[];
  collapsible?: boolean;
}

interface MenuContextProps {
  showMenus: Height | number;
  setShowMenus: (param: Height | number) => void;
}

const MenuContext = createContext<MenuContextProps | null>(null);

const Menu = ({ children }: MenuProps) => {
  const [showMenus, setShowMenus] = useState<Height | number>(0);

  return (
    <MenuContext.Provider value={{ showMenus, setShowMenus }}>
      <div className="menu position-relative">{children}</div>
    </MenuContext.Provider>
  );
};

const Head = ({ children, collapsible = true }: MenuHeadProps) => {
  const { showMenus, setShowMenus } = useContext(MenuContext);

  const handleClickOnHead = (currentShow) => {
    setShowMenus(currentShow);
  };

  return (
    <div
      className="menu-head pointer px-6 py-4 overflow-hidden text-title d-flex align-items-center justify-content-between"
      onClick={() => {
        if (collapsible) {
          if (showMenus === 'auto') handleClickOnHead(0);
          else handleClickOnHead('auto');
        }
      }}
    >
      {children}

      <div className="d-flex align-items-center d-flex align-items-center justify-content-between">
        {showMenus ? (
          <IconChevronUp size="24" fill="dark" />
        ) : (
          <IconChevronDown size="24" fill="dark" />
        )}
      </div>
    </div>
  );
};

const ItemWrapper = ({ children }: MenuProps) => {
  const { showMenus } = useContext(MenuContext);

  return (
    <AnimateHeight
      duration={500}
      height={showMenus}
      className="menu-items d-flex flex-column"
    >
      {Array.isArray(children) ? (
        children.map(
          (child: JSX.Element, index: number): JSX.Element | JSX.Element[] => (
            <div key={index} className="menu-item">
              {React.cloneElement(child, {
                className:
                  'text-title d-flex align-items-center gap-6 text-decoration-none px-8 py-4 overflow-hidden',
              })}
            </div>
          ),
        )
      ) : (
        <div className="menu-item">
          {React.cloneElement(children, {
            className:
              'text-title d-flex align-items-center gap-6 text-decoration-none px-8 py-4 overflow-hidden',
          })}
        </div>
      )}
    </AnimateHeight>
  );
};

Menu.Head = Head;
Menu.ItemWrapper = ItemWrapper;

export default Menu;
